/**
 * DP273-P2a — the Kind of party a contact is, and the old category word.
 *
 * The firm's kinds come from the server (GET /api/contacts/kinds). This file
 * is only the reading of the LEGACY category words, so a contact saved before
 * the Kind existed still opens on the right kind, and so the category word
 * keeps a sensible value when somebody picks a kind for the first time.
 *
 * It mirrors backend/utils/party_kinds.py. Keep the two in step.
 */

// The kinds that do work, and so carry trades. `team` carries a role instead.
export const TRADE_KINDS = ['gc', 'sub', 'supplier', 'interior_designer'];

// One canonical label per kind, for a contact with no category word of its own.
export const CATEGORY_FOR_KIND = {
  client: 'Client',
  gc: 'General Contractor',
  interior_designer: 'Interior Designer',
  sub: 'Subcontractor - Other',
  supplier: 'Vendor/Supplier',
  team: 'Team',
  personal: 'Personal',
  other: 'Other',
};

const CATEGORY_KINDS = {
  client: 'client',
  owner: 'client',
  'spouse/partner': 'client',
  'general contractor': 'gc',
  gc: 'gc',
  contractor: 'gc',
  'vendor/supplier': 'supplier',
  vendor: 'supplier',
  supplier: 'supplier',
  studio_manager: 'team',
  team: 'team',
  personal: 'personal',
  'inspector/official': 'other',
  'business general': 'other',
  other: 'other',
};

const SUB_SUFFIX_TRADES = {
  electrical: 'Electrician',
  plumbing: 'Plumber',
  hvac: 'HVAC',
  carpentry: 'Carpenter',
  tile: 'Tile setter',
  painting: 'Painter',
  roofing: 'Roofer',
};

/** The kind a legacy record reads as. Never throws; unknown words are 'other'. */
export function kindFromCategory(category, contact = {}) {
  const low = String(category || '').trim().toLowerCase();
  if (low.startsWith('subcontractor')) return 'sub';
  if (low in CATEGORY_KINDS) {
    const kind = CATEGORY_KINDS[low];
    if (kind === 'other' && contact.linked_user_id) return 'team';
    if (kind === 'other' && contact.is_vendor) return 'supplier';
    if (kind === 'other' && contact.ownership_tier === 'personal') return 'personal';
    return kind;
  }
  if (contact.is_vendor) return 'supplier';
  if (contact.linked_user_id) return 'team';
  if (contact.ownership_tier === 'personal') return 'personal';
  return 'other';
}

/** The trade a "Subcontractor - Electrical" style word names, if any. */
export function tradeFromCategory(category) {
  const low = String(category || '').trim().toLowerCase();
  if (!low.startsWith('subcontractor')) return null;
  const suffix = low.includes('-') ? low.split('-').slice(1).join('-').trim() : '';
  return SUB_SUFFIX_TRADES[suffix] || null;
}

/**
 * The category word to keep on the record for a kind. A word that ALREADY
 * reads as this kind is the better label and is kept as it is.
 */
export function categoryForKind(kind, currentCategory) {
  if (currentCategory && kindFromCategory(currentCategory) === kind) return currentCategory;
  return CATEGORY_FOR_KIND[kind] || 'Other';
}

/** Does this kind pick trades? */
export function kindCarriesTrades(kind) {
  return TRADE_KINDS.includes(kind);
}

/**
 * DP273-P2b — the kind a row IS. A record saved before the Kind existed is
 * read off its old category word, so no row is left unsorted for that reason
 * alone; a record with neither reads as null and belongs under "Needs a kind".
 */
export function kindOf(contact) {
  if (!contact) return null;
  if (contact.party_kind) return contact.party_kind;
  if (String(contact.category || '').trim()) return kindFromCategory(contact.category, contact);
  return null;
}

/** The word for a kind: the firm's own label, then the two standing ones. */
export function kindLabel(key, kinds = []) {
  if (!key) return 'Needs a kind';
  const found = kinds.find(k => k.key === key);
  if (found?.label) return found.label;
  if (key === 'personal') return 'Personal';
  if (key === 'other') return 'Other';
  return CATEGORY_FOR_KIND[key] || key;
}
